import { Checkbox } from '@domgen/base-design-system-2.0/atoms/Checkbox';
import { ComboBox } from '@domgen/base-design-system-2.0/atoms/ComboBox';
import { Input } from '@domgen/base-design-system-2.0/atoms/Input';
import { CircleCheck } from '../../../assets/icons/CircleCheck';
import { telephoneLocaleCodes } from '../constants/telephoneLocaleCodes';
import { AddressSearchResult } from '@domgen/base-design-system-2.0/molecules/AddressSearchInput';
import { CircleExclamationIcon } from '../../../assets/icons/CircleExclamationIcon';
import { ChangeEvent } from 'react';
import { FormChangeEvent } from '../../../hooks/useFormValidation';
import { gaEvent } from '@domgen/shared/services/ga/ga';
import { usePostHog } from '@posthog/react';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { store } from '../../../store/store';
import { AddressForm } from './AddressForm';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { ApplianceFormData } from '../../../models/form/appliance';
import { AutoComplete } from '@domgen/shared/enums/autoComplete';

interface PersonalDetailsFormProps {
  formValues: Partial<ApplianceFormData[StepKey.PERSONAL_DETAILS]>;
  errors: Record<string, string>;
  handleChange: (e: FormChangeEvent) => string | void;
  onBlur: (e: FormChangeEvent) => string | void;
  onAddressSelect: (address: AddressSearchResult, addressType?: 'mailing' | 'merchandise') => void;
  setMerchandiseLocatedAtSameAddress: (value: boolean) => void;
  merchandiseLocatedAtSameAddress: boolean;
  applianceCategory: string;
  sessionId: string;
  systemCode: string;
}

export function PersonalDetailsForm({
  formValues,
  errors,
  handleChange,
  onBlur,
  onAddressSelect,
  setMerchandiseLocatedAtSameAddress,
  merchandiseLocatedAtSameAddress,
  applianceCategory,
  sessionId,
  systemCode,
}: PersonalDetailsFormProps) {
  const handleCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setMerchandiseLocatedAtSameAddress(e.target.checked);
    if (!e.target.checked) {
      capturePostHogEvent(posthog, 'location-address-unticked', store.get());
    }
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'Same as where my product is located',
      eventLabel: e.target.checked ? 'ticked' : 'unticked',
      type: 'STANDARD',
      pagename: 'olr-personal-details',
      applianceCategory,
      category: 'registration-pages',
      appSessionID: sessionId,
      regSystemCode: systemCode,
    });
  };

  const posthog = usePostHog();
  return (
    <>
      <div className="col-span-8 md:col-span-4">
        <Input
          label="First name"
          placeholder="Enter first name"
          name={FormKey.FIRST_NAME}
          type="text"
          value={formValues?.[FormKey.FIRST_NAME]}
          onChange={handleChange}
          onBlur={onBlur}
          hasError={errors?.[FormKey.FIRST_NAME]}
          rightIcon={
            formValues?.[FormKey.FIRST_NAME] && !errors?.[FormKey.FIRST_NAME] ? (
              <CircleCheck />
            ) : undefined
          }
          hasSuccess={formValues?.[FormKey.FIRST_NAME] !== '' && !errors?.[FormKey.FIRST_NAME]}
          id="firstName"
          autoComplete={AutoComplete.FIRST_NAME}
        />
      </div>
      <div className="col-span-8 md:col-span-4">
        <Input
          label="Last name"
          placeholder="Enter last name"
          name={FormKey.LAST_NAME}
          type="text"
          value={formValues?.[FormKey.LAST_NAME]}
          onChange={handleChange}
          onBlur={onBlur}
          hasError={errors?.[FormKey.LAST_NAME]}
          rightIcon={
            formValues?.[FormKey.LAST_NAME] && !errors?.[FormKey.LAST_NAME] ? (
              <CircleCheck />
            ) : undefined
          }
          hasSuccess={formValues?.[FormKey.LAST_NAME] !== '' && !errors?.[FormKey.LAST_NAME]}
          id="lastName"
          autoComplete={AutoComplete.LAST_NAME}
        />
      </div>
      <AddressForm
        formValues={formValues}
        errors={errors}
        onAddressSelect={onAddressSelect}
        handleFormChange={handleChange}
        onBlur={onBlur}
        addressType="mailing"
        applianceCategory={applianceCategory}
        sessionId={sessionId}
        systemCode={systemCode}
      />
      <div className="col-span-8">
        <Checkbox
          label="Same as where my product is located"
          checked={merchandiseLocatedAtSameAddress}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleCheckbox(e);
            posthog.capture(`location-address-${e.target.checked ? 'ticked' : 'unticked'}`);
          }}
        />
      </div>
      <div className="col-span-8">
        <Input
          label="Email"
          placeholder="Enter email"
          name="email"
          type="email"
          value={formValues?.[FormKey.EMAIL]}
          onChange={handleChange}
          onBlur={onBlur}
          hasError={errors?.[FormKey.EMAIL]}
          rightIcon={
            formValues?.[FormKey.EMAIL] && !errors?.[FormKey.EMAIL] ? <CircleCheck /> : undefined
          }
          hasSuccess={formValues?.[FormKey.EMAIL] !== '' && !errors?.[FormKey.EMAIL]}
          id="email"
          autoComplete={AutoComplete.EMAIL}
        />
      </div>
      <div className="col-span-8">
        <div className="flex flex-col gap-space-200">
          <label htmlFor="phoneNumber" className="text-body-strong">
            Phone number
          </label>
          <div className="inline-flex w-full items-start gap-space-400">
            <div className="flex-1 min-w-[100px]">
              <ComboBox
                autoComplete={AutoComplete.PHONE_DIALLING_CODE}
                defaultValue="+1"
                value={formValues?.[FormKey.PHONE_DIALLING_CODE] ?? '+1'}
                placeholder="+1"
                name={FormKey.PHONE_DIALLING_CODE}
                options={telephoneLocaleCodes}
                hasSuccess={formValues?.[FormKey.PHONE_NUMBER] !== '' && !errors.phoneNumber}
                hasError={!!errors.phoneDiallingCode}
                onSelect={(value) => {
                  handleChange({
                    target: { name: FormKey.PHONE_DIALLING_CODE, value: value ?? '' },
                  });
                }}
              />
            </div>
            <div className="flex-4">
              <Input
                autoComplete={AutoComplete.PHONE_NUMBER}
                placeholder="Enter phone number"
                name={FormKey.PHONE_NUMBER}
                type="tel"
                value={formValues?.[FormKey.PHONE_NUMBER]}
                onChange={handleChange}
                onBlur={onBlur}
                hasError={!!errors?.[FormKey.PHONE_NUMBER]}
                hasSuccess={
                  formValues?.[FormKey.PHONE_NUMBER] !== '' && !errors?.[FormKey.PHONE_NUMBER]
                }
                rightIcon={
                  formValues?.[FormKey.PHONE_NUMBER] && !errors?.[FormKey.PHONE_NUMBER] ? (
                    <CircleCheck />
                  ) : undefined
                }
                id="phoneNumber"
              />
            </div>
          </div>
          {(errors?.[FormKey.PHONE_NUMBER] || errors?.[FormKey.PHONE_DIALLING_CODE]) && (
            <div className="inline-flex items-center w-full text-text-error gap-space-100">
              <CircleExclamationIcon />
              <p className="text-sm" role="alert">
                {errors?.[FormKey.PHONE_NUMBER] || errors?.[FormKey.PHONE_DIALLING_CODE]}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
